// QuickBooks Sales Import gap-fill (docs/SALES_ANALYTICS_MODULE.md). 3-step flow: upload CSV →
// review AI column mapping (mirrors TradeShowDetailPage.js's ImportLeadsMappingModal) → review
// AI customer/product match suggestions, one row per distinct name (mirrors QboSyncPage.js's
// Ingredient Account Sync tab's accept/override pattern) → confirm.
import React, { useState, useRef } from 'react';
import salesImportService from '../services/salesImportService';
import SearchableSelect from './SearchableSelect';
import AiProgressIndicator from '../pages/maintenance/AiProgressIndicator';

const FIELD_LABELS = {
  customerName: 'Customer',
  productName: 'Product / Item',
  transactionDate: 'Date',
  quantity: 'Quantity',
  amount: 'Amount ($)',
};
const FIELD_ORDER = Object.keys(FIELD_LABELS);
const REQUIRED_FIELDS = ['customerName', 'amount'];

export default function SalesImportModal({ onClose, onImported }) {
  const [phase, setPhase] = useState('upload'); // upload | mapping | matching | confirming | done | error
  const [error, setError] = useState('');
  const [csvText, setCsvText] = useState('');
  const [sourceLabel, setSourceLabel] = useState('QuickBooks Sales by Customer Detail');

  const [previewData, setPreviewData] = useState(null); // {headers, mapping, confidence, notes, totalRows, sampleRows}
  const [mapping, setMapping] = useState({});

  const [matchData, setMatchData] = useState(null); // {customerMatches, productMatches, customers, products, rowCount}
  const [customerOverrides, setCustomerOverrides] = useState({}); // rawName -> customerId (string)
  const [productOverrides, setProductOverrides] = useState({}); // rawName -> productId (string) or '' to skip
  const [acceptedCustomers, setAcceptedCustomers] = useState({}); // rawName -> bool
  const [acceptedProducts, setAcceptedProducts] = useState({}); // rawName -> bool

  const [confirmResult, setConfirmResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [matchingMessage, setMatchingMessage] = useState('');
  const fileRef = useRef();

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setError('');
    setBusy(true);
    try {
      const text = await file.text();
      setCsvText(text);
      const data = await salesImportService.preview(text);
      setPreviewData(data);
      setMapping(data.mapping);
      setPhase('mapping');
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to read/preview this CSV.');
    } finally {
      setBusy(false);
    }
  };

  const handleMappingNext = async () => {
    setBusy(true);
    setError('');
    setMatchingMessage('Matching customers and products against your catalog…');
    try {
      const data = await salesImportService.match(csvText, mapping, setMatchingMessage);
      setMatchData(data);
      const acceptC = {};
      data.customerMatches.forEach(m => { if (m.matchedCustomerId && m.confidence === 'high') acceptC[m.name] = true; });
      const acceptP = {};
      data.productMatches.forEach(m => { if (m.matchedProductId && m.confidence === 'high') acceptP[m.name] = true; });
      setAcceptedCustomers(acceptC);
      setAcceptedProducts(acceptP);
      setPhase('matching');
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to match customers/products.');
    } finally {
      setBusy(false);
      setMatchingMessage('');
    }
  };

  const handleConfirm = async () => {
    setBusy(true);
    setError('');
    setPhase('confirming');
    try {
      const custOverrides = {};
      matchData.customerMatches.forEach(m => {
        const chosen = customerOverrides[m.name] ?? (acceptedCustomers[m.name] ? m.matchedCustomerId : '');
        if (chosen) custOverrides[m.name] = Number(chosen);
      });
      const prodOverrides = {};
      matchData.productMatches.forEach(m => {
        const chosen = productOverrides[m.name] ?? (acceptedProducts[m.name] ? m.matchedProductId : '');
        if (chosen) prodOverrides[m.name] = Number(chosen);
      });

      const result = await salesImportService.confirm({
        csvText, mapping, sourceLabel,
        customerOverrides: custOverrides,
        productOverrides: prodOverrides,
      });
      setConfirmResult(result);
      setPhase('done');
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Import failed.');
      setPhase('matching');
    } finally {
      setBusy(false);
    }
  };

  const handleDone = () => {
    onImported?.();
    onClose();
  };

  const canProceedMapping = REQUIRED_FIELDS.every(f => mapping[f]);
  const unmatchedCustomerCount = matchData
    ? matchData.customerMatches.filter(m => !(customerOverrides[m.name] ?? (acceptedCustomers[m.name] ? m.matchedCustomerId : ''))).length
    : 0;

  return (
    <div className="modal fade show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
      <div className="modal-dialog modal-xl modal-dialog-scrollable" role="document">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">📥 Import Sales from QuickBooks</h5>
            <button type="button" className="btn-close" onClick={onClose} disabled={busy} />
          </div>
          <div className="modal-body">
            {error && <div className="alert alert-danger py-2">{error}</div>}

            {phase === 'upload' && (
              <div className="text-center py-5">
                <p className="text-muted mb-3">
                  Upload QuickBooks' "Sales by Customer Detail" report (CSV) to fill in revenue for
                  customers/months quickLOG has no order data for. This never overwrites or
                  duplicates revenue quickLOG already has — only genuine gaps get filled.
                </p>
                <div className="mb-3" style={{ maxWidth: 360, margin: '0 auto' }}>
                  <label className="form-label small text-muted">Source label (optional)</label>
                  <input type="text" className="form-control form-control-sm" value={sourceLabel} onChange={e => setSourceLabel(e.target.value)} />
                </div>
                <input ref={fileRef} type="file" accept=".csv,text/csv" hidden onChange={handleFile} />
                <button className="btn btn-primary" onClick={() => fileRef.current.click()} disabled={busy}>
                  {busy ? <span className="spinner-border spinner-border-sm me-2" /> : null}
                  {busy ? 'Reading…' : 'Choose CSV File'}
                </button>
              </div>
            )}

            {phase === 'mapping' && previewData && (
              <>
                <p className="text-muted small">
                  {previewData.totalRows} row{previewData.totalRows === 1 ? '' : 's'} found. AI matched
                  your CSV's columns to our fields below (confidence: {previewData.confidence}) —
                  review and correct anything before continuing.
                </p>
                {previewData.notes && <div className="alert alert-warning py-2 small">{previewData.notes}</div>}
                <table className="table table-sm">
                  <thead><tr><th>Our Field</th><th>CSV Column</th></tr></thead>
                  <tbody>
                    {FIELD_ORDER.map(field => (
                      <tr key={field}>
                        <td className="small fw-semibold">
                          {FIELD_LABELS[field]}{REQUIRED_FIELDS.includes(field) && <span className="text-danger"> *</span>}
                        </td>
                        <td>
                          <select
                            className="form-select form-select-sm"
                            value={mapping[field] || ''}
                            onChange={e => setMapping(m => ({ ...m, [field]: e.target.value || null }))}
                          >
                            <option value="">— none —</option>
                            {previewData.headers.map(h => <option key={h} value={h}>{h}</option>)}
                          </select>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {previewData.sampleRows?.length > 0 && (
                  <div className="mt-3">
                    <div className="text-muted small mb-1">Sample rows from your file:</div>
                    <div className="table-responsive">
                      <table className="table table-sm table-bordered">
                        <thead><tr>{previewData.headers.map(h => <th key={h} className="small">{h}</th>)}</tr></thead>
                        <tbody>
                          {previewData.sampleRows.map((row, i) => (
                            <tr key={i}>{previewData.headers.map(h => <td key={h} className="small">{row[h]}</td>)}</tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
                <AiProgressIndicator active={!!matchingMessage} message={matchingMessage} />
              </>
            )}

            {phase === 'matching' && matchData && (
              <>
                {matchData.aiMatchFailed && (
                  <div className="alert alert-warning py-2 small">
                    The AI couldn't confidently match names this time (often means the file has a
                    lot of distinct customers/products). Everything below starts unmatched —
                    pick each one manually; no need to re-scan.
                  </div>
                )}
                <p className="text-muted small">
                  {matchData.rowCount} row{matchData.rowCount === 1 ? '' : 's'} total. Review the
                  customer and product matches below — a customer with no match is required before
                  its rows can be imported; a product with no match still counts revenue, just no
                  cost/margin for that line.
                </p>

                <div className="fw-semibold mb-2" style={{ fontSize: 13 }}>
                  Customers {unmatchedCustomerCount > 0 && <span className="badge bg-warning text-dark ms-1">{unmatchedCustomerCount} unmatched</span>}
                </div>
                <table className="table table-sm mb-3">
                  <thead><tr><th style={{ width: 28 }}></th><th>CSV Name</th><th>Matched Customer</th><th>Confidence</th></tr></thead>
                  <tbody>
                    {matchData.customerMatches.map(m => {
                      const currentVal = customerOverrides[m.name] ?? (acceptedCustomers[m.name] ? String(m.matchedCustomerId || '') : '');
                      return (
                        <tr key={m.name}>
                          <td>
                            <input type="checkbox" className="form-check-input" checked={!!currentVal}
                              onChange={e => {
                                if (!e.target.checked) { setAcceptedCustomers(p => ({ ...p, [m.name]: false })); setCustomerOverrides(p => ({ ...p, [m.name]: '' })); }
                                else if (m.matchedCustomerId) setAcceptedCustomers(p => ({ ...p, [m.name]: true }));
                              }} />
                          </td>
                          <td className="small">{m.name}</td>
                          <td>
                            <SearchableSelect
                              size="sm"
                              options={matchData.customers.map(c => ({ value: c.id, label: c.name }))}
                              value={currentVal}
                              placeholder="— no match, skip —"
                              onChange={v => { setCustomerOverrides(p => ({ ...p, [m.name]: v })); setAcceptedCustomers(p => ({ ...p, [m.name]: true })); }}
                            />
                          </td>
                          <td><span className={`badge ${m.confidence === 'high' ? 'bg-success' : m.confidence === 'medium' ? 'bg-warning text-dark' : 'bg-secondary'}`} style={{ fontSize: 10 }}>{m.matchedCustomerId ? m.confidence : 'no match'}</span></td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>

                <div className="fw-semibold mb-2" style={{ fontSize: 13 }}>Products</div>
                <table className="table table-sm">
                  <thead><tr><th style={{ width: 28 }}></th><th>CSV Name</th><th>Matched Product</th><th>Confidence</th></tr></thead>
                  <tbody>
                    {matchData.productMatches.map(m => {
                      const currentVal = productOverrides[m.name] ?? (acceptedProducts[m.name] ? String(m.matchedProductId || '') : '');
                      return (
                        <tr key={m.name}>
                          <td>
                            <input type="checkbox" className="form-check-input" checked={!!currentVal}
                              onChange={e => {
                                if (!e.target.checked) { setAcceptedProducts(p => ({ ...p, [m.name]: false })); setProductOverrides(p => ({ ...p, [m.name]: '' })); }
                                else if (m.matchedProductId) setAcceptedProducts(p => ({ ...p, [m.name]: true }));
                              }} />
                          </td>
                          <td className="small">{m.name}</td>
                          <td>
                            <SearchableSelect
                              size="sm"
                              options={matchData.products.map(p => ({ value: p.id, label: p.name }))}
                              value={currentVal}
                              placeholder="— no match, revenue only —"
                              onChange={v => { setProductOverrides(p => ({ ...p, [m.name]: v })); setAcceptedProducts(p => ({ ...p, [m.name]: true })); }}
                            />
                          </td>
                          <td><span className={`badge ${m.confidence === 'high' ? 'bg-success' : m.confidence === 'medium' ? 'bg-warning text-dark' : 'bg-secondary'}`} style={{ fontSize: 10 }}>{m.matchedProductId ? m.confidence : 'no match'}</span></td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </>
            )}

            {(phase === 'done') && confirmResult && (
              <div className="text-center py-4">
                <div className="mb-2" style={{ fontSize: 32 }}>✅</div>
                <div className="fw-semibold mb-2">Imported {confirmResult.created} row{confirmResult.created === 1 ? '' : 's'}.</div>
                <div className="text-muted small">
                  {confirmResult.skippedAlreadyCovered > 0 && <div>{confirmResult.skippedAlreadyCovered} skipped — quickLOG already had order revenue for that customer/month.</div>}
                  {confirmResult.skippedNoCustomer > 0 && <div>{confirmResult.skippedNoCustomer} skipped — no matched customer.</div>}
                  {confirmResult.skippedNoDate > 0 && <div>{confirmResult.skippedNoDate} skipped — no valid date.</div>}
                  {confirmResult.skippedNoAmount > 0 && <div>{confirmResult.skippedNoAmount} skipped — no valid amount.</div>}
                </div>
              </div>
            )}
          </div>
          <div className="modal-footer">
            {phase !== 'done' && <button className="btn btn-outline-secondary" onClick={onClose} disabled={busy}>Cancel</button>}
            {phase === 'mapping' && (
              <button className="btn btn-primary" onClick={handleMappingNext} disabled={busy || !canProceedMapping}>
                {busy ? <span className="spinner-border spinner-border-sm me-2" /> : null}
                {busy ? 'Matching…' : 'Next: Match Customers & Products'}
              </button>
            )}
            {phase === 'matching' && (
              <button className="btn btn-primary" onClick={handleConfirm} disabled={busy}>
                {busy ? <span className="spinner-border spinner-border-sm me-2" /> : null}
                {busy ? 'Importing…' : 'Confirm Import'}
              </button>
            )}
            {phase === 'confirming' && (
              <button className="btn btn-primary" disabled>
                <span className="spinner-border spinner-border-sm me-2" />Importing…
              </button>
            )}
            {phase === 'done' && <button className="btn btn-primary" onClick={handleDone}>Done</button>}
          </div>
        </div>
      </div>
    </div>
  );
}
